import { zodResolver } from '@hookform/resolvers/zod';
import { Button, MenuItem, TextField, Typography } from '@mui/material';
import { AthletePreview, athleteDisplayName } from '@strength/domain';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer/drawer';
import { Form, FormField, FormItem } from '../../../../components/form/form';
import { FormNotification } from '../../../../components/form/form-notification';
import { SingleSelectCreatable } from '../../../../components/input/SingleSelectCreatable';
import { StringDatePicker } from '../../../../components/input/string-date-picker';
import { useMutation } from '../../../../../loco-sync';
import { toast } from '../../../../hooks/useToast';
import { usePlanContext } from '../../context/PlanContext';
import { useTrackMutations } from '../../hooks/useTrackMutations';
import { blockForm } from '../../timeline/forms/block-form-schema';
import {
  buildTrackInput,
  buildSessionInput,
  buildSessionWorkoutCommand,
} from './create-session-input';
import { singleAthleteTracksForAthlete } from './single-athlete-tracks';

const FORM_ID = 'create-session-form';

const sessionFormSchema = z.object({
  name: blockForm.shape.name,
  trackId: z.string().min(1),
  focusId: z.string().optional(),
  trainingMaxRatio: z.coerce.number().positive().max(1),
});

type SessionFormValues = z.infer<typeof sessionFormSchema>;

export type CreateSessionDrawerProps = {
  open: boolean;
  athlete: AthletePreview | null;
  startDate: string | null;
  onClose: () => void;
};

export function CreateSessionDrawer({
  open,
  athlete,
  startDate,
  onClose,
}: CreateSessionDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId, teamDetail, tracks, focuses } = usePlanContext();
  const blockMutation = useMutation();
  const { createTrack } = useTrackMutations();
  // The ref guards re-entrant submits (Enter submits the form without a
  // click) during the awaited window.
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  // Track committed by an earlier Save whose Block create failed: a retry
  // reuses it while the typed name is unchanged (the shared track list lags
  // the create, so the form value alone can't prove the track exists).
  const committedTrackRef = useRef<{ id: string; name: string } | null>(null);
  // Bumped by handleClose so a close mid-save invalidates the in-flight
  // attempt: the awaited createTrack continuation and the Block callbacks
  // must not touch drawer state or navigate after the drawer closed.
  const saveAttemptRef = useRef(0);

  // SAFETY: z.coerce.number() makes the resolver's input type irreconcilable
  // with the form values; the cast is type-level only.
  const form = useForm<SessionFormValues>({
    resolver: zodResolver(
      sessionFormSchema,
    ) as unknown as Resolver<SessionFormValues>,
    defaultValues: {
      name: '',
      trackId: '',
      focusId: undefined,
      trainingMaxRatio: 1,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Mirrors the live text in the Track field so Save works without Enter.
  const [liveTrackInput, setLiveTrackInput] = useState('');

  const handleClose = () => {
    saveAttemptRef.current += 1;
    isSavingRef.current = false;
    setIsSaving(false);
    form.reset();
    setBannerDismissed(false);
    setLiveTrackInput('');
    committedTrackRef.current = null;
    onClose();
  };

  const trackOptions = useMemo(
    () =>
      athlete
        ? singleAthleteTracksForAthlete(tracks, athlete.id).map((track) => ({
            id: track.id,
            name: track.name,
          }))
        : [],
    [tracks, athlete],
  );

  const onSubmit = async (values: SessionFormValues) => {
    if (!teamDetail || !athlete || !startDate) return;
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    const saveAttempt = saveAttemptRef.current;

    // If trackId doesn't match an existing option the coach typed a new name.
    // Create the track first so the block FK is never broken.
    const isNewTrack = !trackOptions.some((o) => o.id === values.trackId);
    let resolvedTrackId = values.trackId;
    if (isNewTrack) {
      const committed = committedTrackRef.current;
      if (committed && committed.name === liveTrackInput) {
        resolvedTrackId = committed.id;
      } else {
        const trackInput = buildTrackInput({
          name: liveTrackInput,
          profileId: athlete.id,
        });
        try {
          // The Zero payload carries the team id from the plan context; the
          // tRPC path derives it server-side and ignores it. `color: null`
          // matches the schema's server-side default for a new track.
          await createTrack({ ...trackInput, color: null, teamId });
        } catch {
          // A close mid-save already reset the state — only live attempts toast.
          if (saveAttemptRef.current === saveAttempt) {
            toast({ title: t('errorCreatingSession'), severity: 'error' });
          }
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
        if (saveAttemptRef.current !== saveAttempt) return;
        committedTrackRef.current = {
          id: trackInput.id,
          name: liveTrackInput,
        };
        resolvedTrackId = trackInput.id;
      }
      form.setValue('trackId', resolvedTrackId);
    }

    const input = buildSessionInput({
      teamDetail,
      name: values.name,
      trackId: resolvedTrackId,
      startDate,
      focusId: values.focusId ?? null,
      trainingMaxRatio: values.trainingMaxRatio,
    });

    blockMutation.mutate(
      { module: 'Block', type: 'Create', input },
      {
        onSuccess: () => {
          // Stale attempt (closed mid-save): the drawer is gone, nothing to do.
          if (saveAttemptRef.current !== saveAttempt) return;
          blockMutation.mutate(
            buildSessionWorkoutCommand({ blockId: input.id, startDate }),
          );
          handleClose();
          navigate({
            to: '/$teamId/block/$blockId',
            params: { teamId, blockId: input.id },
            search: athlete.id ? { athleteId: athlete.id } : {},
          });
        },
        onError: () => {
          // The track create already committed — a Save retry reuses it (ref above).
          toast({ title: t('errorCreatingSession'), severity: 'error' });
          isSavingRef.current = false;
          setIsSaving(false);
        },
      },
    );
  };

  return (
    <Drawer open={open} onClose={handleClose}>
      <DrawerHeader onClose={handleClose}>
        <Typography variant="h3">{t('createSession')}</Typography>
      </DrawerHeader>

      <Form {...form}>
        <form
          id={FORM_ID}
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 px-6 py-4"
        >
          {!bannerDismissed && (
            <FormNotification
              title={t('sessionVisibility')}
              type="info"
              onDismiss={() => setBannerDismissed(true)}
            >
              {t('sessionMustBePublished')}
            </FormNotification>
          )}

          {athlete && (
            <Typography variant="body1Medium">
              {t('selectedAthlete')}: {athleteDisplayName(athlete)}
            </Typography>
          )}

          <FormField
            name="name"
            control={control}
            render={({ field }) => (
              <FormItem>
                <TextField
                  {...field}
                  label={t('blockName')}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  fullWidth
                />
              </FormItem>
            )}
          />

          <FormField
            name="trackId"
            control={control}
            render={({ field, fieldState }) => (
              <FormItem>
                <SingleSelectCreatable
                  label={t('track')}
                  options={trackOptions}
                  value={trackOptions.find((o) => o.id === field.value) ?? null}
                  onChange={(option) => {
                    setLiveTrackInput(option?.name ?? '');
                    field.onChange(option?.id ?? '');
                  }}
                  onInputChange={(val) => {
                    setLiveTrackInput(val);
                    if (!val) {
                      field.onChange('');
                    } else {
                      const exact = trackOptions.find(
                        (o) => o.name.toLowerCase() === val.toLowerCase(),
                      );
                      // Auto-select when the typed text exactly matches an
                      // existing option; otherwise treat as new-track name.
                      field.onChange(exact ? exact.id : val);
                    }
                  }}
                  createOptionLabel={(name) =>
                    t('createNewTrackOption', { name })
                  }
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              </FormItem>
            )}
          />

          <StringDatePicker
            label={t('startDate')}
            value={startDate ?? undefined}
            onChange={() => {}}
            disabled
          />

          <div className="flex flex-row gap-4">
            <TextField
              label={t('trainingWeeks')}
              value="1"
              disabled
              fullWidth
            />
            <TextField label={t('trainingDays')} value="1" disabled fullWidth />
          </div>

          <FormField
            name="focusId"
            control={control}
            render={({ field }) => (
              <FormItem>
                <TextField
                  label={t('focus')}
                  select
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  fullWidth
                >
                  {(focuses ?? []).map((focus) => (
                    <MenuItem key={focus.id} value={focus.id}>
                      {focus.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormItem>
            )}
          />

          <FormField
            name="trainingMaxRatio"
            control={control}
            render={({ field }) => (
              <FormItem>
                <TextField
                  {...field}
                  type="number"
                  label={t('trainingMaxRatio')}
                  error={Boolean(errors.trainingMaxRatio)}
                  helperText={errors.trainingMaxRatio?.message}
                  fullWidth
                />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <DrawerFooter className="flex flex-row justify-end gap-2 px-6 pb-4">
        <Button variant="outlined" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          disabled={isSaving}
        >
          {t('save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
